import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { animationModes } from '~/app/components/lottie-animation/lib';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { DynamicLottieAnimationProps } from './types';

const getInitialState = (
  currentComponent: any,
  componentPath: string,
  field: string,
  initialValue: any
) => {
  return get(currentComponent, `props.${componentPath}.${field}`, initialValue);
};

type UseDynamicLottieAnimationProps = {
  fieldPath?: string;
};

export const useDynamicLottieAnimation = ({
  fieldPath = '',
}: UseDynamicLottieAnimationProps = {}) => {
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];

  const [dimensions, setDimensions] = useState<
    DynamicLottieAnimationProps['dimensions']
  >({
    width: 300,
    height: 300,
    tablet: {
      width: undefined,
      height: undefined,
    },
    mobile: {
      width: undefined,
      height: undefined,
    },
  });
  const [animationData, setAnimationData] =
    useState<DynamicLottieAnimationProps['animationData']>('');
  const [animationMode, setAnimationMode] = useState<
    DynamicLottieAnimationProps['animationMode']
  >(animationModes.SEEK);
  const [visibility, setVisibility] = useState<
    DynamicLottieAnimationProps['visibility']
  >({ x: '0.5', y: '1.0' });
  const [frames, setFrames] = useState<DynamicLottieAnimationProps['frames']>({
    x: '0.5',
    y: '1.0',
  });

  const handleUpdateComponent = useCallback(
    (field: string, value: string | number | any[]) => {
      const fieldPathValue = fieldPath
        ? `props.${componentPath}.${fieldPath}.${field}` // If fieldPath is provided, update the nested field
        : `props.${componentPath}.${field}`; // If fieldPath is not provided, update the top-level field
      set(currentComponent, fieldPathValue, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, fieldPath, dispatch]
  );

  const handleAnimationDataChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    handleUpdateComponent('animationData', e.target.value);
    setAnimationData(e.target.value);
  };

  const handleAnimationModeChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    handleUpdateComponent('animationMode', e.target.value);
    setAnimationMode(e.target.value);
  };

  const handleVisibilityXChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('visibility.x', e.target.value);
    setVisibility({ ...visibility, x: e.target.value });
  };

  const handleVisibilityYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('visibility.y', e.target.value);
    setVisibility({ ...visibility, y: e.target.value });
  };

  const handleFramesXChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('frames.x', e.target.value);
    setFrames({ ...frames, x: e.target.value });
  };

  const handleFramesYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('frames.y', e.target.value);
    setFrames({ ...frames, y: e.target.value });
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.width', Number(e.target.value));
    setDimensions({ ...dimensions, width: Number(e.target.value) });
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.height', Number(e.target.value));
    setDimensions({ ...dimensions, height: Number(e.target.value) });
  };

  const handleTabletWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.tablet.width', Number(e.target.value));
    setDimensions({
      ...dimensions,
      tablet: { ...dimensions.tablet, width: Number(e.target.value) },
    });
  };

  const handleTabletHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.tablet.height', Number(e.target.value));
    setDimensions({
      ...dimensions,
      tablet: { ...dimensions.tablet, height: Number(e.target.value) },
    });
  };

  const handleMobileWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.mobile.width', Number(e.target.value));
    setDimensions({
      ...dimensions,
      mobile: { ...dimensions.mobile, width: Number(e.target.value) },
    });
  };

  const handleMobileHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('dimensions.mobile.height', Number(e.target.value));
    setDimensions({
      ...dimensions,
      mobile: { ...dimensions.mobile, height: Number(e.target.value) },
    });
  };

  useEffect(() => {
    setDimensions(
      getInitialState(currentComponent, componentPath, 'dimensions', {
        width: 300,
        height: 300,
      })
    );
    setAnimationData(
      getInitialState(currentComponent, componentPath, 'animationData', '')
    );
    setAnimationMode(
      getInitialState(
        currentComponent,
        componentPath,
        'animationMode',
        animationModes.SEEK
      )
    );
    setVisibility({
      x: getInitialState(
        currentComponent,
        componentPath,
        'visibility.x',
        '0.5'
      ),
      y: getInitialState(
        currentComponent,
        componentPath,
        'visibility.y',
        '1.0'
      ),
    });
    setFrames({
      x: getInitialState(currentComponent, componentPath, 'frames.x', '0.5'),
      y: getInitialState(currentComponent, componentPath, 'frames.y', '1.0'),
    });
  }, [
    currentComponent,
    componentPath,
    setDimensions,
    setAnimationData,
    setAnimationMode,
    setVisibility,
    setFrames,
  ]);

  return {
    currentComponent,
    componentPath,
    animationData,
    animationMode,
    visibility,
    frames,
    dimensions,
    handleUpdateComponent,
    handleAnimationDataChange,
    handleAnimationModeChange,
    handleVisibilityXChange,
    handleVisibilityYChange,
    handleFramesXChange,
    handleFramesYChange,
    handleWidthChange,
    handleHeightChange,
    handleTabletWidthChange,
    handleTabletHeightChange,
    handleMobileWidthChange,
    handleMobileHeightChange,
  };
};
